import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Dr. Sarah Mitchell',
    role: 'Chief Operations Officer',
    company: 'Metro Dialysis Network',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face',
    quote: 'MedexOVA reduced our patient no-show rate by 42% in the first quarter. The AI dispatch system handles complex multi-leg trips that used to take our staff hours to coordinate.',
    rating: 5,
    metric: '42% fewer no-shows',
  },
  {
    id: 2,
    name: 'Marcus Johnson',
    role: 'Fleet Owner',
    company: 'Premier NEMT Services',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    quote: 'The instant payouts changed everything for my drivers. Retention is up 60% and we\'re getting more trips than ever through the platform. The credentialing system alone saves us 20 hours a week.',
    rating: 5,
    metric: '60% better retention',
  },
  {
    id: 3,
    name: 'Jennifer Adams',
    role: 'Transportation Director',
    company: 'City of Phoenix',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face',
    quote: 'We needed a partner who could handle ADA compliance at scale. MedexOVA\'s platform gives us complete visibility into every trip with real-time reporting for federal audits.',
    rating: 5,
    metric: '100% audit compliance',
  },
  {
    id: 4,
    name: 'Robert Chen',
    role: 'Independent Driver',
    company: 'MedexOVA Network',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    quote: 'I switched from another platform and my earnings went up 35% with better routes. Getting paid the same day instead of waiting 2 weeks makes a huge difference for my family.',
    rating: 5,
    metric: '35% higher earnings',
  },
  {
    id: 5,
    name: 'Lisa Thompson',
    role: 'VP of Patient Services',
    company: 'Sunrise Healthcare System',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face',
    quote: 'The conversational AI booking system is incredible. Our elderly patients can call and speak naturally - no more confusing phone trees. Patient satisfaction scores are at an all-time high.',
    rating: 5,
    metric: '98% satisfaction rate',
  },
  {
    id: 6,
    name: 'David Williams',
    role: 'Insurance Operations Manager',
    company: 'BlueCross Regional',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face',
    quote: 'The 837/CMS1500 integration eliminated our claims backlog. What used to take weeks now processes in hours. MedexOVA isn\'t just a vendor - they\'re infrastructure we rely on.',
    rating: 5,
    metric: '90% faster claims',
  },
];

export const TestimonialsSection = () => {
  return (
    <section 
      id="testimonials" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0c1222] via-[#0e1525] to-[#0c1222]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Success Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Trusted by Industry Leaders
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Healthcare providers, drivers, and cities are transforming their operations with MedexOVA.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-6 hover:border-neon-cyan/30 transition-all duration-300 group"
              data-testid={`testimonial-${testimonial.id}`}
            >
              {/* Quote Icon */}
              <Quote className="w-8 h-8 text-neon-cyan/30 mb-4" />
              
              {/* Quote Text */}
              <p className="text-slate-300 leading-relaxed mb-6 text-sm">
                "{testimonial.quote}"
              </p>
              
              {/* Metric Badge */}
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 mb-6">
                <span className="text-xs font-mono text-neon-cyan font-semibold">
                  {testimonial.metric}
                </span>
              </div>
              
              {/* Author */}
              <div className="flex items-center space-x-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/10"
                />
                <div className="flex-1">
                  <h4 className="font-heading font-semibold text-white text-sm">
                    {testimonial.name}
                  </h4>
                  <p className="text-xs text-slate-500">
                    {testimonial.role}
                  </p>
                  <p className="text-xs text-neon-cyan">
                    {testimonial.company}
                  </p>
                </div>
                {/* Rating */}
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { value: '500+', label: 'Healthcare Partners' },
            { value: '12,000+', label: 'Active Drivers' },
            { value: '98.4%', label: 'Satisfaction Rate' },
            { value: '847K+', label: 'Trips Completed' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl font-heading font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
